import { EditIcon } from "@root/assets/icons/editIcon";
import DeleteIcon from "@root/assets/icons/delete-02.svg";
import { Button, Flex, Image, Text, Input, Spinner } from "@chakra-ui/react";
import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { ACTION, ROUTE_NAMES } from "@root/constants";
import { StatusBadge } from "@root/components/common/StatusBadge";
import { getStatus } from "@root/utils";
import { formatDate } from "@root/utils/date-helper";
import { FileIcon } from "@root/assets/icons/file-icon";
import { useState, useEffect, useRef } from "react";
import { CommonTooltip } from "@root/components/common/tooltip/CommonTooltip";

// Editable cell component with local state to prevent table re-renders
const EditableCell = ({
  initialValue,
  onSave,
  onCancel,
  isLoading,
  placeholder,
}: {
  initialValue: string;
  onSave: (value: string) => void;
  onCancel: () => void;
  isLoading: boolean;
  placeholder: string;
}) => {
  const [localValue, setLocalValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update local value when initialValue changes (e.g., when starting to edit)
  useEffect(() => {
    setLocalValue(initialValue);
  }, [initialValue]);

  // Focus input when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      // Set cursor to end initially
      const len = localValue.length;
      inputRef.current.setSelectionRange(len, len);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSave(localValue);
    } else if (e.key === "Escape") {
      onCancel();
    }
  };

  return (
    <Flex alignItems="center" gap="8px" width="100%">
      <Input
        ref={inputRef}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        size="sm"
        variant="unstyled"
        border="1px solid"
        borderColor="#40599B"
        borderRadius="6px"
        px="8px"
        py="6px"
        _focus={{
          borderColor: "#40599B",
          boxShadow: "0 0 0 1px #40599B",
          outline: "none",
        }}
        _hover={{
          borderColor: "#40599B",
        }}
        onKeyDown={handleKeyDown}
        flex="1"
        placeholder={placeholder}
      />
      <Flex
        cursor={isLoading ? "not-allowed" : "pointer"}
        onClick={isLoading ? undefined : () => onSave(localValue)}
        opacity={isLoading ? 0.6 : 1}
        p="4px"
        borderRadius="4px"
        _hover={{
          bg: isLoading ? "transparent" : "gray.100",
        }}
        title={placeholder}
      >
        {isLoading ? (
          <Spinner size="sm" color="#40599B" />
        ) : (
          <CheckIcon boxSize={4} color="#40599B" />
        )}
      </Flex>
      <Flex
        cursor="pointer"
        onClick={onCancel}
        p="4px"
        borderRadius="4px"
        _hover={{ bg: "gray.100" }}
        title={placeholder}
      >
        <CloseIcon boxSize={3} color="#40599B" />
      </Flex>
    </Flex>
  );
};

export const DocTableColumns = (
  t: (key: any) => any,
  handleActions: any,
  handleManualProcess: any,
  isFileClickable: any,
  editingRowId: number | null,
  editingValue: string,
  setEditingValue: (value: string) => void,
  handleSaveEdit: (value?: string) => void,
  handleCancelEdit: () => void,
  updateDocumentLoading: boolean,
  pathname: string,
  handleViewHistory: any,
  isPermitSelected?: boolean,
  editingFileId?: number | null
) => {
  return [
    {
      id: "id",
      header: t("fileTable.fileName"),
      cell: (info: any) => {
        const file = info?.row?.original;
        if (!file) return null;

        const isEditing = editingRowId === file?.id;
        // Get hoveredRowId from table meta - with comprehensive safety check
        // During data updates (like after fetchExtractionReviews), table instance might be temporarily unavailable
        let hoveredRowId: number | string | null = null;
        try {
          // Check if table instance exists and is stable before accessing meta
          if (
            info?.table &&
            typeof info.table === "object" &&
            info?.table?.options
          ) {
            const meta = info?.table?.options?.meta;
            if (meta && typeof meta === "object") {
              hoveredRowId = (meta as any)?.hoveredRowId ?? null;
            }
          }
        } catch (error) {
          // Silently handle if meta is not available during re-renders
          hoveredRowId = null;
        }
        const isRowHovered = hoveredRowId === file?.id;

        // Handle isPermitSelected case with multiple files
        if (isPermitSelected && file?.files && Array.isArray(file?.files)) {
          return (
            <Flex alignItems="center" gap="12px" maxW="100%">
              <Flex
                width="32px"
                height="32px"
                borderRadius="10px"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <FileIcon width="32px" height="32px" color="#349AE2" />
              </Flex>
              <Flex flexDir="column" gap="8px" width="100%" flex="1">
                {file?.files?.map((fileItem: any, index: number) => {
                  const isFileEditing = editingFileId === fileItem?.id;
                  const fileName =
                    fileItem?.fileName_key || fileItem?.fileName || "";
                  // Remove "files/" prefix if present
                  const displayName = fileName?.replace(/^files\//, "");

                  if (isFileEditing) {
                    return (
                      <EditableCell
                        key={fileItem?.id || index}
                        initialValue={displayName}
                        onSave={(value) => handleSaveEdit(value)}
                        onCancel={handleCancelEdit}
                        isLoading={updateDocumentLoading}
                        placeholder={t("fileTable.enterFileName")}
                      />
                    );
                  }

                  return (
                    <Flex
                      key={fileItem?.id || index}
                      alignItems="center"
                      gap="6px"
                      width="100%"
                    >
                      <Text fontSize="14px" fontWeight={500} color="#3A3A3A">
                        {displayName}
                      </Text>
                      <CommonTooltip label={t("tooltip.editFileName")}>
                        <Flex
                          opacity={isRowHovered ? 1 : 0}
                          transition="opacity 0.2s"
                          cursor="pointer"
                          onClick={() => handleActions(ACTION.EDIT, fileItem)}
                          p="2px"
                          borderRadius="4px"
                          _hover={{ bg: "gray.100" }}
                          alignItems="center"
                          justifyContent="center"
                          minW="18px"
                          h="18px"
                          flexShrink={0}
                        >
                          <EditIcon />
                        </Flex>
                      </CommonTooltip>
                    </Flex>
                  );
                })}
              </Flex>
            </Flex>
          );
        }

        // Original behavior for non-permit selected case
        if (isEditing) {
          return (
            <EditableCell
              initialValue={file?.name || ""}
              onSave={(value) => handleSaveEdit(value)}
              onCancel={handleCancelEdit}
              isLoading={updateDocumentLoading}
              placeholder={t("fileTable.enterFileName")}
            />
          );
        }

        return (
          <Flex alignItems="center" gap="12px" maxW="100%">
            <Flex
              width="32px"
              height="32px"
              borderRadius="10px"
              // bg="#E9F1FF"
              alignItems="center"
              justifyContent="center"
            >
              <FileIcon width="32px" height="32px" color="#349AE2" />
            </Flex>
            <Text
              flex="1"
              fontSize="14px"
              fontWeight={500}
              color="#3A3A3A"
              noOfLines={isPermitSelected ? undefined : 1}
              textOverflow={isPermitSelected ? undefined : "ellipsis"}
              whiteSpace={isPermitSelected ? "pre-line" : "normal"}
            >
              {file?.name || ""}
            </Text>
          </Flex>
        );
      },
    },
    {
      id: "name",
      header: t("fileTable.date"),
      cell: (info: any) => {
        const row = info?.row?.original;
        // Use createdAt if available (for extraction reviews), otherwise use uploaded
        const dateValue = row?.createdAt || row?.uploaded;
        if (!dateValue) return "";
        // If it's already a formatted string (from uploaded), return it
        // Otherwise format it
        return formatDate(dateValue, "YYYY/MM/DD") || "";
      },
    },
    {
      id: "actions",
      header: t("adminTable.actions"),
      cell: (info: any) => {
        const file = info?.row?.original;

        const renderStatusButton = (row: any) => {
          if (!pathname.includes(ROUTE_NAMES.LEGAL_CHATBOT))
            return (
              <StatusBadge
                status={getStatus(file?.status)}
                onManualProcess={
                  isFileClickable(file?.status)
                    ? () => handleManualProcess(file)
                    : undefined
                }
                variant={isFileClickable(file?.status) ? "button" : "badge"}
              />
            );
        };

        const isEditing = editingRowId === file?.id || editingFileId !== null;

        return (
          <Flex
            gap={"20px"}
            alignItems="center"
            justifyContent="flex-end"
            width="100%"
            data-tour-item="permit-document-actions"
          >
            <>
              {!isEditing && !isPermitSelected && (
                <CommonTooltip label={t("tooltip.editFileName")}>
                  <Flex
                    gap={"8px"}
                    cursor={"pointer"}
                    onClick={() => handleActions(ACTION.EDIT, file)}
                  >
                    <EditIcon />
                  </Flex>
                </CommonTooltip>
              )}
              <CommonTooltip label={t("tooltip.deleteFile")}>
                <Flex
                  gap={"8px"}
                  cursor={"pointer"}
                  onClick={() => handleActions(ACTION.DELETE, file)}
                >
                  <Image src={DeleteIcon} width={18} filter="brightness(0)" />
                </Flex>
              </CommonTooltip>
            </>
            {pathname === ROUTE_NAMES.DRAWING_ANALYSIS && (
              <Button
                bg={"white"}
                color={"#000000"}
                border={"1px solid #6648FF"}
                fontSize={"14px"}
                fontWeight={500}
                onClick={() => handleViewHistory(file)}
                cursor={"pointer"}
                borderRadius="6px"
                height="34px"
                px={3}
                py={0}
                width="fit-content"
                minW="auto"
                _hover={{
                  bg: "white",
                }}
              >
                {t("fileTable.viewHistory")}
              </Button>
            )}
            {renderStatusButton(file)}
          </Flex>
        );
      },
    },
  ];
};
